import { forwardRef, useState } from 'react';

import produce from 'immer';
import styled from 'styled-components';

import { Button, ButtonProps } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { AnimatedCheckbox } from '@components/checkbox';
import { EmptyState } from '@components/empty-state';
import {
  ConditionComparator,
  ConditionOp,
  FieldId,
  FormFieldCondition,
  FormFieldValue,
} from '@components/form/v2';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconCondition } from '@components/icons/condition';
import { IconElement } from '@components/icons/element';
import { IconTrash } from '@components/icons/trash';
import { Menu } from '@components/menu';
import { AnimatedRadio } from '@components/radio-group/animated-radio';
import { Select, SelectOption } from '@components/select';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { getScopedTranslate, styledTranslate, useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';
import { randomId } from '@utils/strings';
import { isValidDateString } from '@utils/validators';

import { useFormBuilderContext } from './context';
import { getFieldConfigForKind } from './field-registry';
import { StateField, StateFieldWithCondition } from './state';

const MENU_WIDTH = pxToRem(308);

const filterableKinds = [
  'input-date',
  'input-dropdown',
  'input-multiple-choice',
  'input-single-choice',
] as const;

type FilterableKind = (typeof filterableKinds)[number];

type FilterableQuestion = Extract<StateField, { kind: FilterableKind }>;

const isFilterableQuestion = (question: StateField): question is FilterableQuestion =>
  filterableKinds.includes(question.kind as FilterableKind);

export const isMultiSelector = (comparator: ConditionComparator) =>
  ['any_of', 'none_of'].includes(comparator);

export type FilterMenuProps = {
  condition?: FormFieldCondition;
  field: StateFieldWithCondition;
  onSave(condition: FormFieldCondition): void;
  onRemove?(condition: FormFieldCondition): void;
};

const QuestionLabel = ({ id, onUnselect }: { id: FieldId; onUnselect(): void }) => {
  const {
    store: { useFormFieldFromId },
  } = useFormBuilderContext();
  const field = useFormFieldFromId<Extract<StateField, { label: any }>>(id);

  return (
    <Menu.Item
      selectionType="open"
      isHeader
      label={field.label.toUpperCase()}
      icon={<IconArrowLeft />}
      onSelect={onUnselect}
    />
  );
};

const useFilterableQuestions = (currentId: FieldId) => {
  const {
    store: { useFlattenedList },
  } = useFormBuilderContext();

  const flattened = useFlattenedList(true);
  const currentIndex = flattened.findIndex((field) => field.id === currentId);
  const flattenedBeforeMe = flattened.slice(0, currentIndex);

  return flattenedBeforeMe.filter(isFilterableQuestion);
};

const getIconForSelection = (isSelected: boolean, isMultiSelector: boolean) => {
  if (isSelected) {
    return isMultiSelector ? <AnimatedCheckbox checked /> : <AnimatedRadio checked />;
  }
  return isMultiSelector ? <AnimatedCheckbox checked={false} /> : <AnimatedRadio checked={false} />;
};

export const ValueMenu = ({
  options,
  comparator,
  value,
  onSelect,
}: {
  options: Array<SelectOption>;
  comparator: ConditionComparator;
  value?: FormFieldValue;
  onSelect(value: FormFieldValue): void;
}) => {
  const isMulti = isMultiSelector(comparator);

  const isSelected = (optionValue: string) => {
    return value === optionValue || (Array.isArray(value) && value.includes(optionValue));
  };

  return (
    <>
      {options.map((option, index) => {
        return (
          <Menu.Item
            icon={getIconForSelection(isSelected(option.value), isMulti)}
            key={`${option.value}_${index}`}
            label={option.label}
            selectionType="open"
            onSelect={() => onSelect(option.value)}
          />
        );
      })}
    </>
  );
};

const QuestionMenu = ({
  condition,
  onClose,
  field,
  onSave,
  onRemove,
}: {
  condition?: FormFieldCondition;
  onClose(): void;
  field: StateFieldWithCondition;
  onSave(condition: FormFieldCondition): void;
  onRemove?(): void;
}) => {
  const [state, setState] = useState<Partial<FormFieldCondition>>(condition ?? { id: randomId() });
  const { t } = useScopedTranslate(conditionsLocale);
  const filterableQuestions = useFilterableQuestions(field.id);

  if (!state.questionId) {
    return (
      <>
        {filterableQuestions.map((question) => {
          const Icon = getFieldConfigForKind(question.kind).Icon;
          return (
            <Menu.Item
              key={question.id}
              label={question.label}
              icon={<Icon />}
              selectionType="open"
              onSelect={() => {
                setState((old) => ({ ...old, questionId: question.id }));
              }}
            />
          );
        })}
      </>
    );
  }

  const onValueSelect = (value: string) => {
    if (!state.comparator) return;
    const isMulti = isMultiSelector(state.comparator);

    setState(
      produce(state, (draft) => {
        if (!isMulti) {
          draft.value = value;
          return;
        }

        if (!value) return;

        const currentValue = (draft.value ?? []) as string[];
        if (currentValue.includes(value)) {
          draft.value = currentValue.filter((v) => v !== value);
        } else {
          draft.value = [...currentValue, value];
        }
      })
    );
  };

  return (
    <>
      <QuestionLabel
        id={state.questionId}
        onUnselect={() => {
          setState((old) => ({ id: old.id }));
        }}
      />

      {state.comparator && (
        <Menu.Item
          selectionType="open"
          isHeader
          label={t(`core.comparator.${state.comparator}`).replace('...', '')}
          icon={<IconArrowLeft />}
          onSelect={() => {
            setState((old) => ({
              id: old.id,
              questionId: old.questionId,
            }));
          }}
        />
      )}

      <Menu.Separator />

      {!state.comparator && (
        <ComparatorMenu
          questionId={state.questionId}
          onSelect={(comparator) => {
            setState((old) => ({ ...old, comparator }));
          }}
        />
      )}

      {state.comparator && (
        <>
          <FieldValueMenu
            questionId={state.questionId}
            comparator={state.comparator}
            value={state.value}
            onSelect={onValueSelect}
          />

          <Menu.Separator />

          <ButtonGroup align="right">
            {condition && (
              <Button
                variant="secondary"
                iconLeft={<IconTrash />}
                text={t('core.remove')}
                onClick={onRemove}
              />
            )}
            <Button
              isDisabled={!Boolean(state.value)}
              variant="primary"
              text={t('core.save')}
              onClick={() => {
                onSave(state as FormFieldCondition);
                onClose();
              }}
            />
          </ButtonGroup>
        </>
      )}
    </>
  );
};

const ConditionMenu = ({ field, condition, onSave, onRemove }: FilterMenuProps) => {
  const removeCondition = () => {
    if (condition) {
      onRemove?.(condition);
    }
  };
  return (
    <Menu
      width={MENU_WIDTH}
      trigger={condition ? AppliedConditionButton : ConditionButton}
      triggerProps={
        condition ? { condition, onRemove: removeCondition, style: { height: 'fit-content' } } : {}
      }
      placement="bottom-start"
    >
      {({ close }) => (
        <QuestionMenu
          condition={condition}
          onClose={close}
          onSave={onSave}
          field={field}
          onRemove={removeCondition}
        />
      )}
    </Menu>
  );
};

const ComparatorMenu = ({
  questionId,
  onSelect,
}: {
  questionId: FieldId;
  onSelect(comparator: ConditionComparator): void;
}) => {
  const {
    store: { useFormFieldFromId },
  } = useFormBuilderContext();
  const { t } = useScopedTranslate(conditionsLocale);
  const field = useFormFieldFromId(questionId);
  const config = getFieldConfigForKind(field.kind);

  if (!config.condition) return null;

  return (
    <>
      {config.condition.comparators.map((comparator) => (
        <Menu.Item
          key={comparator}
          label={t(`core.comparator.${comparator}`)}
          onSelect={() => onSelect(comparator)}
          selectionType="open"
        />
      ))}
    </>
  );
};

const FieldValueMenu = ({
  questionId,
  comparator,
  value,
  onSelect,
}: Pick<FormFieldCondition, 'questionId' | 'comparator'> & {
  value?: FormFieldValue;
  onSelect(value: FormFieldValue): void;
}) => {
  const {
    store: { useFormFieldFromId },
  } = useFormBuilderContext();
  const field = useFormFieldFromId(questionId);
  const config = getFieldConfigForKind(field.kind);

  if (!config.condition) return null;

  return (
    <config.condition.ValueMenu
      field={field}
      comparator={comparator}
      value={value}
      onSelect={onSelect}
    />
  );
};

const AppliedConditionButton = forwardRef<
  HTMLButtonElement,
  { condition: FormFieldCondition; onRemove(): void } & ButtonProps
>(({ condition, onRemove, ...rest }, ref) => {
  const { t } = useScopedTranslate(conditionsLocale);

  const {
    predefinedOptionsLists,
    store: { getState },
  } = useFormBuilderContext();

  const question = getState().fieldMap[condition?.questionId];

  if (!question || !('label' in question)) {
    return null;
  }

  const IconForKind = getFieldConfigForKind(question.kind).Icon;

  const findDisplayValue = (raw: FormFieldValue) => {
    if ('options' in question) {
      if (question.optionsFromListId) {
        const usedList = predefinedOptionsLists.find(
          (list) => list.value === question.optionsFromListId
        );
        return usedList?.options.find((option) => option.value === raw)?.label ?? raw;
      } else {
        return question.options.find((option) => option.value === raw)?.label ?? raw;
      }
    }

    return raw;
  };

  let value = Array.isArray(condition.value)
    ? condition.value.map(findDisplayValue).join(', ')
    : findDisplayValue(condition.value);

  if (typeof value === 'string' && question.kind === 'input-date' && isValidDateString(value)) {
    value = abbreviateDate(value, 'en', 'date-fancy');
  }

  return (
    <StyledConditionButton {...rest} variant="secondary" ref={ref}>
      <ConditionDisplay>
        <IconForKind />

        <div className="condition-text">
          <span className="condition-label">{question.label}</span>
          <span>•</span>
          <span style={{ color: comparatorColor[condition.comparator] }}>
            {t(`core.comparator.${condition.comparator}`).replace('...', '')}
          </span>
          <span>•</span>
          <span className="condition-value">{value}</span>
        </div>

        <div
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onRemove();
          }}
        >
          <IconCloseRegular />
        </div>
      </ConditionDisplay>
    </StyledConditionButton>
  );
});

const comparatorColor: Record<ConditionComparator, string> = {
  is: theme.color.elementGreen200,
  any_of: theme.color.elementGreen200,
  not_is: theme.color.elementRed200,
  none_of: theme.color.elementRed200,
  after: theme.color.elementOrange200,
  before: theme.color.elementOrange200,
  on: theme.color.elementGreen200,
};

const ConditionButton = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { t } = useScopedTranslate(conditionsLocale);
  return (
    <StyledConditionButton
      {...props}
      variant="secondary"
      iconLeft={<IconAddPlus />}
      text={t('add')}
      ref={ref}
    />
  );
});

const ConditionsContent = ({ field }: { field: StateFieldWithCondition }) => {
  const { t } = useScopedTranslate(conditionsLocale);
  const {
    store: { actions },
  } = useFormBuilderContext();
  const state = field.condition;
  const filterableQuestions = useFilterableQuestions(field.id);
  const onChange = actions.createChangeHandler(field, 'condition');

  const updateOperator = (op: ConditionOp) => {
    onChange({ op, conditions: state.conditions });
  };

  const updateCondition = (condition: FormFieldCondition) => {
    onChange({
      op: state.op,
      conditions: state.conditions.map((sc) => {
        if (sc.id === condition.id) return { ...condition };
        return sc;
      }),
    });
  };

  const addCondition = (condition: FormFieldCondition) => {
    onChange({
      op: state.op,
      conditions: [...state.conditions, condition],
    });
  };

  const removeCondition = (condition: FormFieldCondition) => {
    onChange({
      op: state.op,
      conditions: state.conditions.filter((sc) => sc.id !== condition.id),
    });
  };

  const operatorOptions: SelectOption[] = [
    { label: t('AND'), value: 'AND' },
    { label: t('OR'), value: 'OR' },
  ];

  return (
    <Stack space={4} style={{ padding: pxToRem(24) }}>
      <Text font="regular14" text={t('description')} />
      <Separator />
      <Stack orientation="horizontal" space={4} align="center" justify="space-between">
        <Text
          font="regular14"
          text={styledTranslate({
            key: 'display',
            translations: conditionsLocale,
            variables: {
              conjunction: {
                text: t(state.op).toLowerCase(),
                font: 'regular14',
                style: { textDecoration: 'underline' },
              },
            },
          })}
        />
        <ConditionsSelectContainer>
          <Select
            value={state.op}
            options={operatorOptions}
            onChange={(op) => updateOperator(op as ConditionOp)}
            isClearable={false}
            isSearchable={false}
          />
        </ConditionsSelectContainer>
      </Stack>
      <Stack space={2} style={{ flexShrink: 1, minWidth: 0 }}>
        {state.conditions.map((condition) => (
          <ConditionMenu
            key={condition.id}
            field={field}
            condition={condition}
            onSave={updateCondition}
            onRemove={() => removeCondition(condition)}
          />
        ))}
      </Stack>
      {filterableQuestions.length > 0 ? (
        <ConditionMenu field={field} onSave={addCondition} />
      ) : (
        <EmptyContainer>
          <EmptyState
            skipMargin
            variant="small"
            content={t('emptyContent')}
            header={t('emptyHeader')}
            icon={<IconElement size={40} />}
          />
        </EmptyContainer>
      )}
    </Stack>
  );
};

const conditionsLocale = {
  en: {
    add: 'Add element',
    title: 'Conditions',
    display: 'Display this element if {conjunction} of the following matches',
    AND: 'All',
    OR: 'Any',
    emptyContent: 'Conditions can be created from preceding elements with select options',
    emptyHeader: 'No applicable elements',
    description:
      'Only display this element when specific rules are met, if you want to show or hide multiple elements we recommend putting them inside a section',
  },
};

export const conditionsTab = (field: StateFieldWithCondition) => {
  const { t } = getScopedTranslate(conditionsLocale);

  return {
    label: t('title'),
    icon: IconCondition,
    tabCount: field.condition.conditions.length,
    content: <ConditionsContent field={field} />,
  };
};

const EmptyContainer = styled.div({
  marginRight: pxToRem(-24),
});

const ConditionsSelectContainer = styled.div({
  flexShrink: 0,
  width: pxToRem(100),
});

const ConditionDisplay = styled.div({
  display: 'flex',
  minWidth: 0,
  alignItems: 'center',
  gap: pxToRem(4),
  font: theme.font.semibold14,
  color: theme.color.textPrimary,

  '.condition-text': {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    flexWrap: 'wrap',
    gap: pxToRem(2),
  },

  '.condition-value': {
    color: theme.color.elementBlue100,
  },

  '.condition-value, .condition-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

const StyledConditionButton = styled(Button)({
  maxWidth: '100%',
  height: 'auto',
  minHeight: theme.base.height,
  padding: pxToRem(8),
});
